"use client";

import { useEffect, useState } from "react";
import { apiPut } from "@/lib/api";
import { useToast } from "@/components/toast-provider";

const MAX_LEN = 24;

/** Compact Accounts-page field. Saved on blur; empty clears the stored nickname. */
export function AccountNicknameField({
  accountHash,
  value,
  placeholder,
  dimmed,
  onSaved,
}: {
  accountHash: string;
  value: string | null | undefined;
  placeholder?: string;
  dimmed?: boolean;
  onSaved?: (next: string) => void;
}) {
  const { showToast } = useToast();
  const [text, setText] = useState(value ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(value ?? "");
  }, [value]);

  const save = async () => {
    const next = text.trim().slice(0, MAX_LEN);
    const prev = (value ?? "").trim();
    if (next !== text) setText(next);
    if (next === prev || saving) return;
    setSaving(true);
    try {
      await apiPut(`/accounts/${encodeURIComponent(accountHash)}/nickname`, {
        nickname: next,
      });
      onSaved?.(next);
    } catch (e) {
      setText(value ?? "");
      showToast("error", e instanceof Error ? e.message : "Could not save nickname");
    } finally {
      setSaving(false);
    }
  };

  return (
    <input
      type="text"
      value={text}
      maxLength={MAX_LEN}
      placeholder={placeholder || "notify name"}
      title="Used in push notifications instead of the account name"
      disabled={saving}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => void save()}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      className={`w-[96px] h-5 px-1.5 text-xs rounded border border-border bg-surface-light text-foreground placeholder-muted focus:outline-none focus:border-cyan disabled:opacity-50 ${
        dimmed ? "opacity-40" : ""
      }`}
    />
  );
}
